import type { PRNG, WordSlot, GridLayout, Difficulty } from "./types.js";
import { getWordsByLength } from "./word-filter.js";

export interface GridResult {
  grid: (string | null)[][];
  filledSlots: WordSlot[];
  layout: GridLayout;
}

/**
 * Cell occupancy tracking: which slot index occupies each cell, or -1 if empty.
 */
type OccupancyGrid = number[][];

interface PlacedWord {
  slot: WordSlot;
  index: number;
}

/**
 * Sample an index from a bell curve (normal distribution) centered on a given
 * fraction of the array. This allows words at any position to be selected,
 * but makes words near the center much more likely.
 *
 * @param count Total number of items
 * @param center Center of the bell curve as a fraction (0-1) of the array
 * @param spread Standard deviation as a fraction of the array (controls width)
 * @param rng Seeded PRNG
 */
function bellCurveIndex(count: number, center: number, spread: number, rng: PRNG): number {
  const mean = center * count;
  const stddev = spread * count;
  let idx: number;
  do {
    const u1 = rng.next();
    const u2 = rng.next();
    const z = Math.sqrt(-2 * Math.log(Math.max(u1, 1e-10))) * Math.cos(2 * Math.PI * u2);
    idx = Math.round(mean + z * stddev);
  } while (idx < 0 || idx >= count);
  return idx;
}

/**
 * Get the bell curve center and spread for a difficulty level.
 * Seven levels space the center from very-common (0.10) to very-rare (0.88).
 * Spread narrows slightly at extremes to keep word selection in range.
 */
function getDifficultyParams(difficulty: Difficulty): { center: number; spread: number } {
  switch (difficulty) {
    case "chill":  return { center: 0.10, spread: 0.20 };
    case "easy":   return { center: 0.22, spread: 0.22 };
    case "breezy": return { center: 0.34, spread: 0.24 };
    case "medium": return { center: 0.48, spread: 0.25 };
    case "tricky": return { center: 0.62, spread: 0.24 };
    case "hard":   return { center: 0.76, spread: 0.22 };
    case "brutal": return { center: 0.88, spread: 0.20 };
  }
}

/**
 * Shuffle a word list using bell-curve weighted sampling.
 * Words near the difficulty center appear first in the shuffled list,
 * but any word can appear anywhere.
 */
function weightedShuffle(words: string[], difficulty: Difficulty, rng: PRNG): string[] {
  const { center, spread } = getDifficultyParams(difficulty);
  const result: string[] = [];
  const remaining = [...words];

  while (remaining.length > 0) {
    const idx = bellCurveIndex(remaining.length, center, spread, rng);
    const clampedIdx = Math.max(0, Math.min(remaining.length - 1, idx));
    result.push(remaining[clampedIdx]);
    remaining.splice(clampedIdx, 1);
  }

  return result;
}

/**
 * Builds a crossword grid dynamically:
 * 1. Place two max-length anchor words (one across, one down) sharing a letter
 * 2. Place remaining words of random lengths, each connecting to an existing word
 * 3. All words must be contiguous (connected graph)
 */
export function buildGrid(
  size: "7x7" | "9x9",
  rng: PRNG,
  maxAttempts = 100,
  difficulty: Difficulty = "medium"
): GridResult | null {
  const dim = size === "7x7" ? 7 : 9;
  const targetWords = size === "7x7" ? 6 : 11;
  const maxLen = dim;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = tryBuildGrid(dim, targetWords, maxLen, rng, difficulty);
    if (result) return result;
  }

  return null;
}

function tryBuildGrid(
  dim: number,
  targetWords: number,
  maxLen: number,
  rng: PRNG,
  difficulty: Difficulty = "medium"
): GridResult | null {
  const occupancy: OccupancyGrid = Array.from({ length: dim }, () =>
    Array(dim).fill(-1)
  );
  const placedWords: PlacedWord[] = [];
  const usedWords = new Set<string>();

  // Step 1: Place first anchor word (across, max length) near center
  const acrossWords = weightedShuffle(getWordsByLength(maxLen), difficulty, rng);

  const word1 = acrossWords[0];
  if (!word1) return null;

  const row1 = rng.nextInt(1, dim - 2); // leave room for vertical connections
  const col1 = 0;
  const slot1: WordSlot = {
    startRow: row1,
    startCol: col1,
    direction: "across",
    length: maxLen,
    word: word1,
  };

  placeWord(occupancy, slot1, 0);
  placedWords.push({ slot: slot1, index: 0 });
  usedWords.add(word1);

  // Step 2: Place second anchor word (down, max length) crossing the first
  const downWords = weightedShuffle(getWordsByLength(maxLen), difficulty, rng);

  let placed2 = false;
  for (const word2 of downWords) {
    if (usedWords.has(word2)) continue;

    // Find a shared letter between word1 and word2
    const crossPositions: { acrossPos: number; downPos: number }[] = [];
    for (let ai = 0; ai < word1.length; ai++) {
      for (let di = 0; di < word2.length; di++) {
        if (word1[ai] === word2[di]) {
          // The down word's row start = row1 - di, so it must be >= 0
          // and row1 - di + maxLen - 1 < dim
          const downStartRow = row1 - di;
          if (downStartRow >= 0 && downStartRow + maxLen <= dim) {
            crossPositions.push({ acrossPos: ai, downPos: di });
          }
        }
      }
    }

    if (crossPositions.length === 0) continue;

    const cross = crossPositions[rng.nextInt(0, crossPositions.length)];
    const downStartRow = row1 - cross.downPos;
    const downCol = col1 + cross.acrossPos;

    const slot2: WordSlot = {
      startRow: downStartRow,
      startCol: downCol,
      direction: "down",
      length: maxLen,
      word: word2,
    };

    // Verify no conflicts (except at the crossing point)
    if (!canPlace(occupancy, slot2, dim, placedWords)) continue;

    placeWord(occupancy, slot2, 1);
    placedWords.push({ slot: slot2, index: 1 });
    usedWords.add(word2);
    placed2 = true;
    break;
  }

  if (!placed2) return null;

  // Step 3: Place remaining words with random lengths, evenly distributed
  const remainingCount = targetWords - 2;
  const wordLengths = generateEvenLengths(remainingCount, 3, maxLen, rng);

  for (const targetLen of wordLengths) {
    const placed = tryPlaceConnectedWord(
      occupancy,
      placedWords,
      usedWords,
      dim,
      targetLen,
      rng,
      difficulty
    );
    if (!placed) {
      // Try adjacent lengths as fallback
      const fallbackLengths = [];
      for (let delta = 1; delta <= 3; delta++) {
        if (targetLen + delta <= maxLen) fallbackLengths.push(targetLen + delta);
        if (targetLen - delta >= 3) fallbackLengths.push(targetLen - delta);
      }
      rng.shuffle(fallbackLengths);

      let fallbackPlaced = false;
      for (const fbLen of fallbackLengths) {
        if (
          tryPlaceConnectedWord(
            occupancy,
            placedWords,
            usedWords,
            dim,
            fbLen,
            rng,
            difficulty
          )
        ) {
          fallbackPlaced = true;
          break;
        }
      }
      if (!fallbackPlaced) return null;
    }
  }

  // Verify contiguity
  if (!isContiguous(placedWords)) return null;

  // Build the final grid
  const grid: (string | null)[][] = Array.from({ length: dim }, () =>
    Array(dim).fill(null)
  );
  const layout: GridLayout = Array.from({ length: dim }, () =>
    Array(dim).fill(false)
  );
  const filledSlots: WordSlot[] = [];

  for (const pw of placedWords) {
    filledSlots.push(pw.slot);
    for (let i = 0; i < pw.slot.length; i++) {
      const r =
        pw.slot.direction === "down"
          ? pw.slot.startRow + i
          : pw.slot.startRow;
      const c =
        pw.slot.direction === "across"
          ? pw.slot.startCol + i
          : pw.slot.startCol;
      grid[r][c] = pw.slot.word[i];
      layout[r][c] = true;
    }
  }

  return { grid, filledSlots, layout };
}

/**
 * Generate evenly distributed random lengths between min and max (inclusive).
 */
function generateEvenLengths(
  count: number,
  min: number,
  max: number,
  rng: PRNG
): number[] {
  const range = max - min + 1;
  const lengths: number[] = [];

  // Distribute evenly across the range, then shuffle
  for (let i = 0; i < count; i++) {
    lengths.push(min + (i % range));
  }
  rng.shuffle(lengths);
  return lengths;
}

/**
 * Try to place a word of the given length that connects to an existing word.
 * Returns true if successful.
 */
function tryPlaceConnectedWord(
  occupancy: OccupancyGrid,
  placedWords: PlacedWord[],
  usedWords: Set<string>,
  dim: number,
  targetLen: number,
  rng: PRNG,
  difficulty: Difficulty = "medium"
): boolean {
  // Collect all candidate crossing points from existing words
  const candidates: {
    existingWordIdx: number;
    existingPos: number;
    row: number;
    col: number;
    existingDir: "across" | "down";
  }[] = [];

  for (const pw of placedWords) {
    for (let i = 0; i < pw.slot.length; i++) {
      const r =
        pw.slot.direction === "down" ? pw.slot.startRow + i : pw.slot.startRow;
      const c =
        pw.slot.direction === "across"
          ? pw.slot.startCol + i
          : pw.slot.startCol;

      candidates.push({
        existingWordIdx: pw.index,
        existingPos: i,
        row: r,
        col: c,
        existingDir: pw.slot.direction,
      });
    }
  }

  rng.shuffle(candidates);

  const wordCandidates = weightedShuffle(getWordsByLength(targetLen), difficulty, rng);

  for (const cand of candidates) {
    const newDir: "across" | "down" =
      cand.existingDir === "across" ? "down" : "across";
    const crossChar =
      placedWords.find((pw) => pw.index === cand.existingWordIdx)!.slot.word[
        cand.existingPos
      ];

    // Try each position in the new word where it could cross
    const positionsInNewWord: number[] = [];
    for (let p = 0; p < targetLen; p++) {
      positionsInNewWord.push(p);
    }
    rng.shuffle(positionsInNewWord);

    for (const posInNew of positionsInNewWord) {
      // Calculate where the new word would start
      let startRow: number, startCol: number;
      if (newDir === "across") {
        startRow = cand.row;
        startCol = cand.col - posInNew;
      } else {
        startRow = cand.row - posInNew;
        startCol = cand.col;
      }

      // Bounds check
      if (startRow < 0 || startCol < 0) continue;
      if (newDir === "across" && startCol + targetLen > dim) continue;
      if (newDir === "down" && startRow + targetLen > dim) continue;

      // Find words that fit this slot with the constraint at the crossing point
      for (const word of wordCandidates) {
        if (usedWords.has(word)) continue;
        if (word[posInNew] !== crossChar) continue;

        const slot: WordSlot = {
          startRow,
          startCol,
          direction: newDir,
          length: targetLen,
          word,
        };

        if (!canPlace(occupancy, slot, dim, placedWords)) continue;

        // Check that it doesn't create adjacent parallel words (ugly crossword style)
        if (hasAdjacentParallel(occupancy, slot, dim)) continue;

        const newIndex = placedWords.length;
        placeWord(occupancy, slot, newIndex);
        placedWords.push({ slot, index: newIndex });
        usedWords.add(word);
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if placing this word would create side-by-side parallel words
 * (letters adjacent but not crossing). This makes grids look bad.
 */
function hasAdjacentParallel(
  occupancy: OccupancyGrid,
  slot: WordSlot,
  dim: number
): boolean {
  for (let i = 0; i < slot.length; i++) {
    const r = slot.direction === "down" ? slot.startRow + i : slot.startRow;
    const c = slot.direction === "across" ? slot.startCol + i : slot.startCol;

    // Check cells perpendicular to this word's direction
    if (slot.direction === "across") {
      // Check above and below
      if (r > 0 && occupancy[r - 1][c] >= 0 && occupancy[r][c] < 0) {
        // There's a letter above, but this cell isn't a crossing point
        return true;
      }
      if (r < dim - 1 && occupancy[r + 1][c] >= 0 && occupancy[r][c] < 0) {
        return true;
      }
    } else {
      // Check left and right
      if (c > 0 && occupancy[r][c - 1] >= 0 && occupancy[r][c] < 0) {
        return true;
      }
      if (c < dim - 1 && occupancy[r][c + 1] >= 0 && occupancy[r][c] < 0) {
        return true;
      }
    }
  }

  // Also check that the word doesn't extend an existing word
  // (no letter directly before or after in the same direction)
  if (slot.direction === "across") {
    const beforeCol = slot.startCol - 1;
    const afterCol = slot.startCol + slot.length;
    if (beforeCol >= 0 && occupancy[slot.startRow][beforeCol] >= 0) return true;
    if (afterCol < dim && occupancy[slot.startRow][afterCol] >= 0) return true;
  } else {
    const beforeRow = slot.startRow - 1;
    const afterRow = slot.startRow + slot.length;
    if (beforeRow >= 0 && occupancy[beforeRow][slot.startCol] >= 0) return true;
    if (afterRow < dim && occupancy[afterRow][slot.startCol] >= 0) return true;
  }

  return false;
}

/**
 * Check if a word can be placed without conflicts.
 * A cell can be shared only if it's a crossing (different direction) and the letters match.
 */
function canPlace(
  occupancy: OccupancyGrid,
  slot: WordSlot,
  dim: number,
  placedWords: PlacedWord[]
): boolean {
  for (let i = 0; i < slot.length; i++) {
    const r = slot.direction === "down" ? slot.startRow + i : slot.startRow;
    const c = slot.direction === "across" ? slot.startCol + i : slot.startCol;

    if (r < 0 || r >= dim || c < 0 || c >= dim) return false;

    const existingIdx = occupancy[r][c];
    if (existingIdx >= 0) {
      // Cell is occupied — only OK if it's a valid crossing
      const existing = placedWords.find((pw) => pw.index === existingIdx);
      if (!existing) return false;
      if (existing.slot.direction === slot.direction) return false; // same direction = overlap

      // Letters must match at crossing
      const existingChar = getCharAt(existing.slot, r, c);
      if (existingChar !== slot.word[i]) return false;
    }
  }
  return true;
}

function getCharAt(slot: WordSlot, row: number, col: number): string {
  if (slot.direction === "across") {
    return slot.word[col - slot.startCol];
  } else {
    return slot.word[row - slot.startRow];
  }
}

function placeWord(
  occupancy: OccupancyGrid,
  slot: WordSlot,
  index: number
): void {
  for (let i = 0; i < slot.length; i++) {
    const r = slot.direction === "down" ? slot.startRow + i : slot.startRow;
    const c = slot.direction === "across" ? slot.startCol + i : slot.startCol;
    // Don't overwrite a crossing point
    if (occupancy[r][c] < 0) {
      occupancy[r][c] = index;
    }
  }
}

/**
 * Verify all words form a connected graph (no floating words).
 */
function isContiguous(placedWords: PlacedWord[]): boolean {
  if (placedWords.length <= 1) return true;

  // Build adjacency: two words are connected if they share a cell
  const adj = new Map<number, Set<number>>();
  for (const pw of placedWords) {
    adj.set(pw.index, new Set());
  }

  // Check all pairs for shared cells
  for (let a = 0; a < placedWords.length; a++) {
    for (let b = a + 1; b < placedWords.length; b++) {
      if (wordsIntersect(placedWords[a].slot, placedWords[b].slot)) {
        adj.get(placedWords[a].index)!.add(placedWords[b].index);
        adj.get(placedWords[b].index)!.add(placedWords[a].index);
      }
    }
  }

  // BFS from first word
  const visited = new Set<number>();
  const queue = [placedWords[0].index];
  visited.add(placedWords[0].index);

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const neighbor of adj.get(current) ?? []) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  return visited.size === placedWords.length;
}

function wordsIntersect(a: WordSlot, b: WordSlot): boolean {
  const aCells = new Set<string>();
  for (let i = 0; i < a.length; i++) {
    const r = a.direction === "down" ? a.startRow + i : a.startRow;
    const c = a.direction === "across" ? a.startCol + i : a.startCol;
    aCells.add(`${r},${c}`);
  }

  for (let i = 0; i < b.length; i++) {
    const r = b.direction === "down" ? b.startRow + i : b.startRow;
    const c = b.direction === "across" ? b.startCol + i : b.startCol;
    if (aCells.has(`${r},${c}`)) return true;
  }

  return false;
}
